export const SWISS_GERMAN_LOCALE = "de-ch"

// Captures each markup tag separately so attribute values (href, src) keep their spelling.
const MARKUP_PART = /<(?:[^>"']|"[^"]*"|'[^']*')*>|[^<]+/g
const URL_VALUE = /^(?:(?:https?:)?\/\/|data:)/i
const IDENTIFIER_KEY = /^(?:_?id|.+_id)$/
const SHARP_S = /[ßẞ]/

export function isSwissGerman(language?: string): boolean {
  return language?.toLowerCase().replace(/_/g, "-") === SWISS_GERMAN_LOCALE
}

export function toSwissGermanSpelling(value: string): string {
  return value.replace(/ß/g, "ss").replace(/ẞ/g, "SS")
}

function rewriteText(value: string): string {
  if (!SHARP_S.test(value) || URL_VALUE.test(value)) {
    return value
  }
  if (!value.includes("<")) {
    return toSwissGermanSpelling(value)
  }
  return value.replace(MARKUP_PART, part => part.startsWith("<") ? part : toSwissGermanSpelling(part))
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") {
    return false
  }
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function rewriteDeep(value: unknown, key?: string): unknown {
  if (typeof value === "string") {
    return key !== undefined && IDENTIFIER_KEY.test(key) ? value : rewriteText(value)
  }
  if (Array.isArray(value)) {
    return value.map(item => rewriteDeep(item, key))
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([entryKey, entry]) => [entryKey, rewriteDeep(entry, entryKey)]))
  }
  return value
}

export function toSwissGermanSpellingDeep<T>(value: T): T {
  return rewriteDeep(value) as T
}

// Structurally an i18next PostProcessorModule, so this package needs no i18next dependency.
export const swissGermanPostProcessor = {
  type: "postProcessor" as const,
  name: "swissGerman",
  process(value: string, _key: string | string[], options: {lng?: string}, translator: {language?: string}): string {
    return typeof value === "string" && isSwissGerman(options?.lng ?? translator?.language) ? toSwissGermanSpelling(value) : value
  }
}
